/* eslint-disable react/display-name */
import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import {
  Switch,
  Route,
  useHistory,
  useLocation,
  useRouteMatch,
} from "react-router-dom";
import { css, StyleSheet } from "aphrodite/no-important";
import { DataGrid, TabsContainer, TabItem, PopupMenu, DatePicker, Loader } from "quantux-ui";
// ___ Actions
import {
  setLoadingCaseData,
  getCaseSchedulesData,
  initiateNewCaseSchedule,
  setSelectedPractice,
  toggleCbLoader,
  loadCaseBlocksData,
  getProvidersSchedule,
  addAppAlert,
  setCaseScheduleDateRangeFilterStartDate,
  setCaseScheduleDateRangeFilterEndDate
} from "../actions";
// ___ Local Imports
import PPAGrid from "../components/ppa-grid";
import GanttGrid from "../components/gantt-grid";
import WarningDialog from "../components/warning-dialog";
import {
  BASE_URL,
  padZero,
  DRAWER_BREAK_POINT,
  titleCase,
  findFlag,
  FLAG_VALUE,
  debugLogToConsole,
  parseDateFromIsoString
} from "../utils";
// ___ Icons and Color Theme
import { gray, blue } from "../theme";
import noPinIcon from "../imgs/no-pin.svg";
import leftPinIcon from "../imgs/left-pin.svg";
import rightPinIcon from "../imgs/right-pin.svg";
import CaseCriticalIcon from "../components/svg-icons/case-critical-icon";
import CaseWarningIcon from "../components/svg-icons/case-warning-icon";
import CaseSuccessIcon from "../components/svg-icons/case-success-icon";
import DocumentNoneIcon from "../components/svg-icons/document-none-icon";
import DocumentSuccessIcon from "../components/svg-icons/document-success-icon";
import DocumentWarningIcon from "../components/svg-icons/document-warning-icon";
import DocumentCriticalIcon from "../components/svg-icons/document-critical-icon";

const DOCS_PER_PAGE = 50;

const DEFAULT_CELL_STYLE = {
  font: "14px Lato",
  padding: "8px",
  display: "flex",
  alignItems: "center",
  lineHeight: "14px",
};

const TABS_TEXT_STYLES = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  fontSize: "12px",
};

const ICONS = {
  filter: { isSvg: false, path: "", class: "fa fa-filter" },
  stringSortAscending: { isSvg: false, class: "fa fa-alpha-asc" },
  stringSortDescending: { isSvg: false, class: "fa fa-alpha-desc" },
  numberSortAscending: { isSvg: false, class: "fa fa-numeric-asc" },
  numberSortDescending: { isSvg: false, class: "fa fa-numeric-desc" },
  dateSortAscending: { isSvg: false, class: "fa fa-alpha-asc" },
  dateSortDescending: { isSvg: false, class: "fa fa-alpha-desc" },
  leftPin: { isSvg: true, path: leftPinIcon },
  rightPin: { isSvg: true, path: rightPinIcon },
  noPin: { isSvg: true, path: noPinIcon },
  menuIcon: { isSvg: false, class: "fa fa-ellipsis-v" },
};

const HEADER_DEFS = [
  // Location - 78px
  {
    // Data field, Display text etc.
    text: "Location",
    field: "room",
    key: "room",
    dataType: "string",
    // Appearance
    visible: true,
    pin: "left",
    isPinnedOnMobile: true,
    // Sorting
    sortable: true,
    sortOrder: 0,
    // Filter
    filter: false,
    isCustomFilter: false,
    showFilterHint: false,
    filterType: "exactMatch",
    filterMenuType: "basic", // possible  options => basic, advanced, none
    basicFilterType: "contains", // possible values are => startsWith, contains, exactMatch, endsWith
    advancedFilterType: "containsAny", // possible values are => select, containsAll, conainsAny
    disabledFilterTypes: [
      "contains",
      "exactMatch",
      "startsWith",
      "endsWith",
      "advanced",
    ],
    // Cell click behavior
    enableCellClick: false,
    // Styles
    colStyles: { width: 170, minWidth: 170, maxWidth: 170, textAlign: "left" },
    colCellsStyles: { textAlign: "left" },
    // Custom cell renderer function
    // cellRenderer: (rawData) => { }
  },
  // Scheduled Times - 123px
  {
    // Data field, Display text etc.
    text: "Sch Times",
    field: "scheduledTimes",
    key: "scheduledTimes",
    dataType: "string",
    // Appearance
    visible: true,
    pin: "none",
    isPinnedOnMobile: true,
    // Sorting
    sortable: false,
    sortOrder: 0,
    // Filter
    filter: false,
    isCustomFilter: false,
    showFilterHint: false,
    filterType: "exactMatch",
    filterMenuType: "basic", // possible  options => basic, advanced, none
    basicFilterType: "contains", // possible values are => startsWith, contains, exactMatch, endsWith
    advancedFilterType: "containsAny", // possible values are => select, containsAll, conainsAny
    disabledFilterTypes: [
      "contains",
      "exactMatch",
      "startsWith",
      "endsWith",
      "advanced",
    ],
    // Cell click behavior
    enableCellClick: false,
    // Styles
    colStyles: { width: 120, minWidth: 120, maxWidth: 120, textAlign: "left" },
    colCellsStyles: { textAlign: "left" },
    // Custom cell renderer function
    // cellRenderer: (rawData) => { }
  },
  // Surgeon(s) - 169px
  {
    // Data field, Display text etc.
    text: "Surgeon",
    field: "surgeons",
    key: "surgeon",
    dataType: "string",
    // Appearance
    visible: true,
    pin: "none",
    isPinnedOnMobile: true,
    // Sorting
    sortable: false,
    sortOrder: 0,
    // Filter
    filter: false,
    isCustomFilter: false,
    showFilterHint: false,
    filterType: "exactMatch",
    filterMenuType: "basic", // possible  options => basic, advanced, none
    basicFilterType: "contains", // possible values are => startsWith, contains, exactMatch, endsWith
    advancedFilterType: "containsAny", // possible values are => select, containsAll, conainsAny
    disabledFilterTypes: [
      "contains",
      "exactMatch",
      "startsWith",
      "endsWith",
      "advanced",
    ],
    // Cell click behavior
    enableCellClick: false,
    // Styles
    colStyles: { width: 100, minWidth: 100, maxWidth: 100, textAlign: "left" },
    colCellsStyles: { textAlign: "left" },
    // Custom cell renderer function
    cellRenderer: (data) => {
      if (Array.isArray(data)) {
        const els = [];
        for (let i = 0; i < data.length; i++) {
          const parts = data[i].fullName.split(',');
          if (i <= 1) {
            els.push(
              <div
                // key={data[i].id || data[i]._id}
                key={Math.random().toString(36).slice(2)}
                style={{ color: blue[500] }}
              >
                {/* {`${titleCase(data[i].fullName)}`} */}
                {`${parts[1].trim().charAt(0).toUpperCase()} ${titleCase(parts[0].trim())}`}
              </div>,
            );
          } else {
            els.push(
              <div
                style={{ color: blue[200] }}
                key={Math.random().toString(36).slice(2)}
              >
                {`(${data.length} Surgeons)`}
              </div>,
            );
          }
        }
        return (
          <div className="cell-scroller">
            {els}
            {/* {data.map((el) => (
              <div key={el.id || el._id}>
                {`•${titleCase(el.fullName)}${el.npi === 0 ? ' ⚠︎' : ' ✓'} `}
              </div>
            ))} */}
          </div>
        );
      }
      return null;
    },
  },
  // Anesthesia - 169px
  {
    // Data field, Display text etc.
    text: "Anesthesia",
    field: "anesthesiologists",
    key: "anesthesiologist",
    dataType: "string",
    // Appearance
    visible: true,
    pin: "none",
    isPinnedOnMobile: true,
    // Sorting
    sortable: false,
    sortOrder: 0,
    // Filter
    filter: false,
    isCustomFilter: false,
    showFilterHint: false,
    filterType: "exactMatch",
    filterMenuType: "basic", // possible  options => basic, advanced, none
    basicFilterType: "contains", // possible values are => startsWith, contains, exactMatch, endsWith
    advancedFilterType: "containsAny", // possible values are => select, containsAll, conainsAny
    disabledFilterTypes: [
      "contains",
      "exactMatch",
      "startsWith",
      "endsWith",
      "advanced",
    ],
    // Cell click behavior
    enableCellClick: false,
    // Styles
    colStyles: { width: 150, minWidth: 150, maxWidth: 150, textAlign: "left" },
    colCellsStyles: { textAlign: "left" },
    // Custom cell renderer function
    cellRenderer: (data) => {
      const { anesthesiologists, crnas } = data;
      const names = [];
      // Anesthesiologists
      const aParts = anesthesiologists.split('|');
      aParts.forEach((el, idx) => {
        if (el !== 'None') {
          const nameParts = el.split(',');
          if (nameParts && Array.isArray(nameParts) && nameParts.length > 1) {
            names.push(
              <div key={`${el}_${idx}`}>
                {`A: ${nameParts[1].trim().charAt(0).toUpperCase()} ${titleCase(nameParts[0].trim())}`}
              </div>
            );
          }
        }
      });

      // Crnas
      const cParts = crnas.split('|');
      cParts.forEach((el, idx) => {
        if (el !== 'None') {
          const nameParts = el.split(',');
          if (nameParts && Array.isArray(nameParts) && nameParts.length > 1) {
            names.push(
              <div key={`${el}_${idx}`}>
                {`C: ${nameParts[1].trim().charAt(0).toUpperCase()} ${titleCase(nameParts[0].trim())}`}
              </div>
            );
          }
        }
      });
      return names;
    }
  },
  // CRNA/CAA - 169px
  {
    // Data field, Display text etc.
    text: "CRNA/CAA",
    field: "crnas",
    key: "crnas",
    dataType: "string",
    // Appearance
    visible: false,
    pin: "none",
    isPinnedOnMobile: true,
    // Sorting
    sortable: false,
    sortOrder: 0,
    // Filter
    filter: false,
    isCustomFilter: false,
    showFilterHint: false,
    filterType: "exactMatch",
    filterMenuType: "basic", // possible  options => basic, advanced, none
    basicFilterType: "contains", // possible values are => startsWith, contains, exactMatch, endsWith
    advancedFilterType: "containsAny", // possible values are => select, containsAll, conainsAny
    disabledFilterTypes: [
      "contains",
      "exactMatch",
      "startsWith",
      "endsWith",
      "advanced",
    ],
    // Cell click behavior
    enableCellClick: false,
    // Styles
    colStyles: { width: 169, minWidth: 169, maxWidth: 169, textAlign: "left" },
    colCellsStyles: { textAlign: "left" },
    // Custom cell renderer function
    // cellRenderer: (rawData) => { }
  },
  // Patient - 160px
  {
    // Data field, Display text etc.
    text: "Patient",
    field: "patient",
    key: "patient",
    dataType: "string",
    // Appearance
    visible: true,
    pin: "none",
    isPinnedOnMobile: true,
    // Sorting
    sortable: false,
    sortOrder: 0,
    // Filter
    filter: false,
    isCustomFilter: false,
    showFilterHint: false,
    filterType: "exactMatch",
    filterMenuType: "basic", // possible  options => basic, advanced, none
    basicFilterType: "contains", // possible values are => startsWith, contains, exactMatch, endsWith
    advancedFilterType: "containsAny", // possible values are => select, containsAll, conainsAny
    disabledFilterTypes: [
      "contains",
      "exactMatch",
      "startsWith",
      "endsWith",
      "advanced",
    ],
    // Cell click behavior
    enableCellClick: false,
    // Styles
    colStyles: { width: 160, minWidth: 160, maxWidth: 160, textAlign: "left" },
    colCellsStyles: { textAlign: "left" },
    // Custom cell renderer function
    // cellRenderer: (rawData) => { }
  },
  // Procedure - 300px
  {
    // Data field, Display text etc.
    text: "Procedure",
    field: "procedures",
    key: "procedure",
    dataType: "string",
    // Appearance
    visible: true,
    pin: "none",
    isPinnedOnMobile: true,
    // Sorting
    sortable: false,
    sortOrder: 0,
    // Filter
    filter: false,
    isCustomFilter: false,
    showFilterHint: false,
    filterType: "exactMatch",
    filterMenuType: "basic", // possible  options => basic, advanced, none
    basicFilterType: "contains", // possible values are => startsWith, contains, exactMatch, endsWith
    advancedFilterType: "containsAny", // possible values are => select, containsAll, conainsAny
    disabledFilterTypes: [
      "contains",
      "exactMatch",
      "startsWith",
      "endsWith",
      "advanced",
    ],
    // Cell click behavior
    enableCellClick: false,
    // Styles
    colStyles: { width: 300, minWidth: 300, maxWidth: 300, textAlign: "left" },
    colCellsStyles: { textAlign: "left" },
    // Custom cell renderer function
    cellRenderer: (data) => {
      if (Array.isArray(data)) {
        return data.map((el) => (
          <div key={el._id || el.id}>
            {el.description}
          </div>
        ));
      }
      return null;
    },
  },
  // AnesStart - 83px
  {
    // Data field, Display text etc.
    text: "AnesStart",
    field: "anesStart",
    key: "anesStart",
    dataType: "string",
    // Appearance
    visible: true,
    pin: "none",
    isPinnedOnMobile: true,
    // Sorting
    sortable: false,
    sortOrder: 0,
    // Filter
    filter: false,
    isCustomFilter: false,
    showFilterHint: false,
    filterType: "exactMatch",
    filterMenuType: "basic", // possible  options => basic, advanced, none
    basicFilterType: "contains", // possible values are => startsWith, contains, exactMatch, endsWith
    advancedFilterType: "containsAny", // possible values are => select, containsAll, conainsAny
    disabledFilterTypes: [
      "contains",
      "exactMatch",
      "startsWith",
      "endsWith",
      "advanced",
    ],
    // Cell click behavior
    enableCellClick: false,
    // Styles
    colStyles: { width: 83, minWidth: 83, maxWidth: 83, textAlign: "left" },
    colCellsStyles: { textAlign: "left" },
    cellRenderer: ({ data, flag }) => {
      return (
        <div style={{ color: flag === FLAG_VALUE.critical ? "yellow" : "" }}>
          {data}
        </div>
      );
    },
  },
  // SurgStart - 83px
  {
    // Data field, Display text etc.
    text: "SurgStart",
    field: "surgStart",
    key: "surgStart",
    dataType: "string",
    // Appearance
    visible: true,
    pin: "none",
    isPinnedOnMobile: true,
    // Sorting
    sortable: false,
    sortOrder: 0,
    // Filter
    filter: false,
    isCustomFilter: false,
    showFilterHint: false,
    filterType: "exactMatch",
    filterMenuType: "basic", // possible  options => basic, advanced, none
    basicFilterType: "contains", // possible values are => startsWith, contains, exactMatch, endsWith
    advancedFilterType: "containsAny", // possible values are => select, containsAll, conainsAny
    disabledFilterTypes: [
      "contains",
      "exactMatch",
      "startsWith",
      "endsWith",
      "advanced",
    ],
    // Cell click behavior
    enableCellClick: false,
    // Styles
    colStyles: { width: 83, minWidth: 83, maxWidth: 83, textAlign: "left" },
    colCellsStyles: { textAlign: "left" },
    cellRenderer: ({ data, flag }) => {
      return (
        <div style={{ color: flag === FLAG_VALUE.critical ? "yellow" : "" }}>
          {data}
        </div>
      );
    },
  },
  // SurgEnd - 83px
  {
    // Data field, Display text etc.
    text: "SurgEnd",
    field: "surgEnd",
    key: "surgEnd",
    dataType: "string",
    // Appearance
    visible: true,
    pin: "none",
    isPinnedOnMobile: true,
    // Sorting
    sortable: false,
    sortOrder: 0,
    // Filter
    filter: false,
    isCustomFilter: false,
    showFilterHint: false,
    filterType: "exactMatch",
    filterMenuType: "basic", // possible  options => basic, advanced, none
    basicFilterType: "contains", // possible values are => startsWith, contains, exactMatch, endsWith
    advancedFilterType: "containsAny", // possible values are => select, containsAll, conainsAny
    disabledFilterTypes: [
      "contains",
      "exactMatch",
      "startsWith",
      "endsWith",
      "advanced",
    ],
    // Cell click behavior
    enableCellClick: false,
    // Styles
    colStyles: { width: 83, minWidth: 83, maxWidth: 83, textAlign: "left" },
    colCellsStyles: { textAlign: "left" },
    cellRenderer: ({ data, flag }) => {
      return (
        <div style={{ color: flag === FLAG_VALUE.critical ? "yellow" : "" }}>
          {data}
        </div>
      );
    },
  },
  // AnesEnd - 83px
  {
    // Data field, Display text etc.
    text: "AnesEnd",
    field: "anesEnd",
    key: "anesEnd",
    dataType: "string",
    // Appearance
    visible: true,
    pin: "none",
    isPinnedOnMobile: true,
    // Sorting
    sortable: false,
    sortOrder: 0,
    // Filter
    filter: false,
    isCustomFilter: false,
    showFilterHint: false,
    filterType: "exactMatch",
    filterMenuType: "basic", // possible  options => basic, advanced, none
    basicFilterType: "contains", // possible values are => startsWith, contains, exactMatch, endsWith
    advancedFilterType: "containsAny", // possible values are => select, containsAll, conainsAny
    disabledFilterTypes: [
      "contains",
      "exactMatch",
      "startsWith",
      "endsWith",
      "advanced",
    ],
    // Cell click behavior
    enableCellClick: false,
    // Styles
    colStyles: { width: 83, minWidth: 83, maxWidth: 83, textAlign: "left" },
    colCellsStyles: { textAlign: "left" },
    cellRenderer: ({ data, flag }) => {
      return (
        <div style={{ color: flag === FLAG_VALUE.critical ? "yellow" : "" }}>
          {data}
        </div>
      );
    },
  },
  // Status/Action - 129px
  {
    // Data field, Display text etc.
    text: "Status/Action",
    field: "status",
    key: "status",
    dataType: "string",
    // Appearance
    visible: true,
    pin: "right",
    isPinnedOnMobile: true,
    // Sorting
    sortable: false,
    sortOrder: 0,
    // Filter
    filter: false,
    isCustomFilter: false,
    showFilterHint: false,
    filterType: "exactMatch",
    filterMenuType: "basic", // possible  options => basic, advanced, none
    basicFilterType: "contains", // possible values are => startsWith, contains, exactMatch, endsWith
    advancedFilterType: "containsAny", // possible values are => select, containsAll, conainsAny
    disabledFilterTypes: [
      "contains",
      "exactMatch",
      "startsWith",
      "endsWith",
      "advanced",
    ],
    // Cell click behavior
    enableCellClick: false,
    // Styles
    colStyles: { width: 150, minWidth: 150, maxWidth: 150, textAlign: "left" },
    colCellsStyles: { textAlign: "left" },
    cellRenderer: ({
      i,
      openPopupMenu,
      history,
      _id,
      caseFlag,
      docsFlag,
      showCaseWarning,
      status = "",
      statusChangeDate = "",
    }) => {
      let piece = null;
      if (status === "cancelled") {
        const tmp = statusChangeDate.split("T");
        const tmp2 = tmp[0].split("-");
        piece = (
          <div
            style={{
              fontSize: "10px",
              display: "flex",
              flexFlow: "column",
              justifyContent: "center",
              minWidth: "67px",
            }}
          >
            <div>
              <span style={{ fontSize: "18px" }}>⊘</span> Cancelled
            </div>
            <div>{`${tmp2[1]}/${tmp2[2]}/${tmp2[0]}`}</div>
          </div>
        );
      }
      if (status === "sent") {
        const tmp = statusChangeDate.split("T");
        const tmp2 = tmp[0].split("-");
        piece = (
          <div
            style={{
              position: "relative",
              left: "-1px",
              fontSize: "11.5px",
              fontWeight: "700",
              lineHeight: "16px",
              display: "flex",
              flexFlow: "column",
              justifyContent: "center",
              minWidth: "67px",
            }}
          >
            <div>Finalized</div>
            <div>{`${tmp2[1]}/${tmp2[2]}/${tmp2[0]}`}</div>
          </div>
        );
      } else if (status === "scheduled") {
        // Case (critial icon) and Patient, Payers & Docs (warning icon)
        piece = (
          <div
            className="fx fx__v-center"
            style={{
              position: "relative",
              left: "-4px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "64px",
              minWidth: "67px",
            }}
          >
            {docsFlag === FLAG_VALUE.none && <DocumentNoneIcon height={20} />}
            {docsFlag === FLAG_VALUE.success && <DocumentSuccessIcon height={20} />}
            {docsFlag === FLAG_VALUE.warning && <DocumentWarningIcon height={20} />}
            {docsFlag === FLAG_VALUE.critical && <DocumentCriticalIcon height={20} />}
            &nbsp;&nbsp;&nbsp;&nbsp;
            {caseFlag === FLAG_VALUE.critical && <CaseCriticalIcon height={20} />}
            {caseFlag === FLAG_VALUE.warning && <CaseWarningIcon height={20} />}
            {caseFlag === FLAG_VALUE.success && <CaseSuccessIcon height={20} />}
          </div>
        );
      }

      return (
        <div className="fx">
          {piece}
          {/* Expand & Ellipsis Icons */}
          <div
            className="fx fx__hv-center"
            style={{
              height: "64px",
              width: "66px",
              borderLeft: `1px solid ${gray[600]}`,
            }}
          >
            {/* Expand icon */}
            <div className="ml-2">
              <svg
                width="20"
                height="20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                onClick={() => {
                  const readOnlyMode =
                    status === "cancelled" || status === "sent";
                  history.push(`/schedule/${_id}/${readOnlyMode}`);
                }}
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M2 0a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2H2Zm12.509 5h-3.587a.491.491 0 1 0 0 .982h2.359l-2.594 2.59a.49.49 0 1 0 .695.694l2.634-2.63v2.337a.491.491 0 0 0 .983 0V5.49a.49.49 0 0 0-.49-.49Zm-5.43 9.016H6.72l2.594-2.59a.49.49 0 1 0-.695-.695l-2.635 2.631v-2.337a.491.491 0 0 0-.983 0v3.482c0 .272.22.492.492.492h3.587a.491.491 0 0 0 0-.983Z"
                  fill="#C0E0FF"
                />
              </svg>
            </div>
            {/* Ellipsis */}
            <div className="ml-3">
              <svg
                width="20"
                height="20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                onClick={(e) => {
                  console.log("Clicked on ellipsis icon!");
                  openPopupMenu(e, i);
                }}
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M2 0a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2H2Zm2.167 8.333a1.667 1.667 0 1 0 0 3.334 1.667 1.667 0 0 0 0-3.334Zm10 1.667a1.667 1.667 0 1 1 3.333 0 1.667 1.667 0 0 1-3.333 0ZM10 8.333a1.667 1.667 0 1 0 0 3.334 1.667 1.667 0 0 0 0-3.334Z"
                  fill="#C0E0FF"
                />
              </svg>
            </div>
          </div>
        </div>
      );
    },
  },
];

// --- Schedule Page
const Schedule = (props) => {
  const [gridStyle, setGridStyle] = useState(0); // 0 -> Rows, & 1 -> Cards
  // ---- Data Grid
  const [showLoader, setShowLoader] = useState(false);
  const [gridData, setGridData] = useState([]);
  const [filters, _] = useState({});
  // const [sorting, setSorting] = useState({});
  const [docsCount, setDocsCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [gridWidth, setGridWidth] = useState(
    window.innerWidth <= 768 ? window.innerWidth - 16 : 550,
  );
  const [gridHeight, setGridHeight] = useState(window.innerHeight - 210);
  const [isMobileLayout, setMobileLayout] = useState(false);
  const [popupMenuIndex, setPopupMenuIndex] = useState(-1);
  const [showGridEllipsisMenu, toggleGridEllipsisMenu] = useState(false);
  const [showGridToolbarMenu, toggleGridToolbarMenu] = useState(false);
  const [cursorX, setCursorX] = useState(0);
  const [cursorY, setCursorY] = useState(0);
  // -- Add Case Drawer
  const [showCancelWarningDialog, setShowCancelWarningDialog] = useState(false);
  const [showFinalizeWarningDialog, setShowFinalizeWarningDialog] =
    useState(false);
  // ===
  const [isPracticeMenuOpen, setIsPracticeMenuOpen] = useState(false);
  // PDF Download
  const [isDownloadingCaseDocs, setIsDownloadingCaseDocs] = useState(false);
  // Schedule day date (Start)
  const [scheduleDate, setScheduleDate] = useState(props.scheduleDate);
  const [scheduleMonth, setScheduleMonth] = useState(props.scheduleMonth);
  const [scheduleYear, setScheduleYear] = useState(props.scheduleYear);
  // Schedule day date (End)
  const [scheduleEndDate, setScheduleEndDate] = useState(props.scheduleEndDate);
  const [scheduleEndMonth, setScheduleEndMonth] = useState(props.scheduleEndMonth);
  const [scheduleEndYear, setScheduleEndYear] = useState(props.scheduleEndYear);
  //
  const [showNoPdfWarningDialog, setShowNoPdfWarningDialog] = useState(false);
  //
  const [isDownloadingPdfBatch, setIsDownloadingPdfBatch] = useState(false);
  // Delete case warning dialog
  const [showDeleteCaseWarning, setShowDeleteCaseWarning] = useState(false);

  // == Refs ============================================================
  const gridParentRef = useRef();
  const gridEllipsisMenu = React.createRef();
  const gridToolbarMenuRef = React.createRef();
  const practiceMenuRef = React.createRef();

  // == Props =========================================================
  const { selectedPractice } = props;

  // == React Router ===================================================
  const history = useHistory();
  const location = useLocation();
  const { pathname } = location;
  const { path, url } = useRouteMatch();

  const handleWindowResize = () => {
    if (gridParentRef && gridParentRef.current) {
      const gridParentRefWidth =
        gridParentRef.current.getBoundingClientRect().width;
      const tmp =
        window.innerWidth <= 768 ? window.innerWidth - 16 : gridParentRefWidth;
      setGridWidth(Math.min(tmp, 1482));
      setGridHeight(window.innerHeight - 210);
      setMobileLayout(window.innerWidth <= DRAWER_BREAK_POINT);
    }
  };
  const dealyedHandleWindowResize = () => {
    setTimeout(handleWindowResize, 500);
  };

  // ------------------------ Network Ops ------------------------
  const submitCase = async (scheduleId) => {
    const formData = new FormData();
    formData.append("scheduleId", scheduleId);

    const reqHeaders = new Headers();
    const token = localStorage.getItem("token");
    reqHeaders.append("Authorization", `Bearer ${token}`);
    const requestOptions = {
      method: "POST",
      body: formData,
      redirect: "follow",
      headers: reqHeaders,
    };
    const response = await fetch(
      `${BASE_URL}/casesSchedule/submitCase`,
      requestOptions,
    );
    const result = await response.json();

    if (result.success) {
      console.log("Case submitted successfully!");
      console.log('Position 1');
      props.getCaseSchedulesData(
        props.selectedPractice,
        scheduleDate,
        scheduleMonth,
        scheduleYear,
        scheduleEndDate,
        scheduleEndMonth,
        scheduleEndYear,
        currentPage
      );
    }
  };

  // ------------------------ Effects ------------------------
  useEffect(() => {
    window.addEventListener("resize", dealyedHandleWindowResize);
    props.setLoadingCaseData(true);
    console.log('Position 2');
    props.getCaseSchedulesData(
      props.selectedPractice,
      scheduleDate,
      scheduleMonth,
      scheduleYear,
      scheduleEndDate,
      scheduleEndMonth,
      scheduleEndYear,
      currentPage
    );
    handleWindowResize();
    return () => {
      window.removeEventListener("resize", dealyedHandleWindowResize);
    };
  }, []);

  useEffect(() => {
    // Case schedules count
    setDocsCount(props.caseSchedulesCount);

    // Case schdules
    const data = [];
    for (let i = 0; i < props.caseSchedules.length; i++) {
      const activeRecord = props.caseSchedules[i];
      let anesthesiologists = "";
      let crnas = "";
      let facility = "";
      let scheduledTimes = "TBD";
      let surgeons = "";
      let procedures = "";
      const {
        faceSheetDocs,
        isNoneFaceSheet,
        consentFormDocs,
        isNoneConsentFormDocs,
        consentSurgFormDocs,
        isNoneConsentSurgFormDocs,
        preOpFormDocs,
        intraOpFormDocs,
        postOpFormDocs,
        isNonePreOpFormDocs,
        isNoneIntraOpFormDocs,
        isNonePostOpFormDocs,
        labsDocs,
        isNoneLabDocs,
        isNoneEkgDocs,
        ekgDocs,
        imagingDocs,
        isNoneImagingDocs,
        consultsDocs,
        isNoneConsults,
        nursingDocs,
        isNoneNursingDocs,
        isNoneSurgeonDocs,
        surgeonDocs,
        ordersPreOpDocs,
        isNoneOrdersPreOpDocs,
        isNoneOrdersIntraOpDocs,
        ordersIntraOpDocs,
        ordresPostOpDocs,
        isNoneOrdersPostOpDocs,
        dischargeDocs,
        isNoneDischargeDocs
      } = activeRecord;

      // ------------- Anesthesiologist(s) & Other Anesthesiologist(s) -------------
      if (activeRecord.anesthesiologists.length > 0) {
        activeRecord.anesthesiologists.forEach((el, idx) => {
          anesthesiologists += `${activeRecord.anesthesiologists.length > 1 && activeRecord.anesthesiologists.length - 1 === idx ? '|' : ''}${el.fullName}`;
        });
      }
      if (activeRecord.otherAnesthesiologists.length > 0) {
        activeRecord.otherAnesthesiologists.forEach((el) => {
          anesthesiologists += `${el.fullName}` + "\n";
        });
      }
      if (activeRecord.isNoneAnesthesiologist) {
        anesthesiologists += "None";
      }

      // ------------- Crna(s) and Other Crna(s) -------------
      if (activeRecord.crnas.length > 0) {
        activeRecord.crnas.forEach((el, idx) => {
          crnas += `${activeRecord.crnas.length > 1 && activeRecord.crnas.length - 1 === idx ? '|' : ''}${el.fullName}`;
        });
      }
      if (activeRecord.otherCrnas && activeRecord.otherCrnas.length > 0) {
        activeRecord.otherCrnas.forEach((el) => {
          crnas += `${el.fullName}` + "\n";
        });
      }
      if (activeRecord.isNoneCrna) {
        crnas += "None";
      }

      // ------------- Surgeons -------------
      const tmpSurgeons = [];
      if (activeRecord.surgeons.length > 0) {
        activeRecord.surgeons.forEach((el) => {
          surgeons += `${el.fullName}` + "\n";
          tmpSurgeons.push(el);
        });
      }

      // ------------- Surgical Procedures ------------
      const tmpProcs = [];
      if (activeRecord.surgicalProcedures.length > 0) {
        activeRecord.surgicalProcedures.forEach((el) => {
          procedures += `${el.description}` + "\n";
          tmpProcs.push(el);
        });
      }

      // ------------- Surgical Procedures -------------
      if (activeRecord.otherSurgicalProcedures.length > 0) {
        activeRecord.otherSurgicalProcedures.forEach((el) => {
          procedures += `${el.description}` + "\n";
          tmpProcs.push(el);
        });
      }

      // ------------- All other data fields -------------
      facility =
        activeRecord.facility && activeRecord.facility.facilityName
          ? `${activeRecord.facility.facilityName} \n ${activeRecord.selectedFacilityLocation}`
          : "";
      const {
        scheduleStartDate = undefined,
        scheduleStartHour: startHour = undefined,
        scheduleStartMinute: startMin = undefined,
        scheduleEndHour: endHour = undefined,
        scheduleEndMinute: endMin = undefined,
      } = activeRecord;

      if (
        startHour !== undefined &&
        startMin !== undefined &&
        endHour !== undefined &&
        endMin !== undefined &&
        scheduleStartDate !== undefined
      ) {
        const { year, month, date } = parseDateFromIsoString(scheduleStartDate);
        scheduledTimes = `${padZero(+month + 1)}/${padZero(date)}/${year}\n${padZero(startHour)}${padZero(startMin)} to ${padZero(endHour)}${padZero(endMin)}`;
      }

      const {
        anesthesiaStartHour = null,
        anesthesiaStartMinute = null,
        anesthesiaEndHour = null,
        anesthesiaEndMinute = null,
        surgeryStartHour = null,
        surgeryStartMinute = null,
        surgeryEndHour = null,
        surgeryEndMinute = null,
        piFlag,
        siFlag,
        tiFlag,
        patientFlag
      } = activeRecord;

      const anesthesiaStartFlag =
        anesthesiaStartHour !== null && anesthesiaStartMinute !== null
          ? FLAG_VALUE.success
          : FLAG_VALUE.critical;
      const anesthesiaEndFlag =
        anesthesiaEndHour !== null && anesthesiaEndMinute !== null
          ? FLAG_VALUE.success
          : FLAG_VALUE.critical;
      const surgeryStartFlag =
        surgeryStartHour !== null && surgeryStartMinute !== null
          ? FLAG_VALUE.success
          : FLAG_VALUE.critical;
      const surgeryEndFlag =
        surgeryEndHour !== null && surgeryEndMinute !== null
          ? FLAG_VALUE.success
          : FLAG_VALUE.critical;
      // Docs Flags
      let faceSheetDocFlag = FLAG_VALUE.critical;
      if (isNoneFaceSheet) {
        faceSheetDocFlag = FLAG_VALUE.none;
      } else {
        faceSheetDocFlag = faceSheetDocs.length > 0 ? FLAG_VALUE.success : FLAG_VALUE.critical;
      }

      let labDocFlag = FLAG_VALUE.critical;
      if (isNoneLabDocs) {
        labDocFlag = FLAG_VALUE.none;
      } else {
        labDocFlag = labsDocs.length > 0 ? FLAG_VALUE.success : FLAG_VALUE.critical;
      }

      let ekgDocFlag = FLAG_VALUE.critical;
      if (isNoneEkgDocs) {
        ekgDocFlag = FLAG_VALUE.none;
      } else {
        ekgDocFlag = ekgDocs.length > 0 ? FLAG_VALUE.success : FLAG_VALUE.critical;
      }

      let imagingDocFlag = FLAG_VALUE.critical;
      if (isNoneImagingDocs) {
        imagingDocFlag = FLAG_VALUE.none;
      } else {
        imagingDocFlag = imagingDocs.length > 0 ? FLAG_VALUE.success : FLAG_VALUE.critical;
      }

      let consultsDocFlag = FLAG_VALUE.critical;
      if (isNoneConsults) {
        consultsDocFlag = FLAG_VALUE.none;
      } else {
        consultsDocFlag = consultsDocs.length > 0 ? FLAG_VALUE.success : FLAG_VALUE.critical;
      }

      let anesConsentDocFlag = FLAG_VALUE.critical;
      if (isNoneConsentFormDocs) {
        anesConsentDocFlag = FLAG_VALUE.none;
      } else {
        anesConsentDocFlag = consentFormDocs.length > 0 ? FLAG_VALUE.success : FLAG_VALUE.success;
      }

      let surgConsentDocFlag = FLAG_VALUE.critical;
      if (isNoneConsentSurgFormDocs) {
        surgConsentDocFlag = FLAG_VALUE.none;
      } else {
        surgConsentDocFlag = consentSurgFormDocs.length > 0 ? FLAG_VALUE.success : FLAG_VALUE.critical;
      }

      let nursingDocFlag = FLAG_VALUE.critical;
      if (isNoneNursingDocs) {
        nursingDocFlag = FLAG_VALUE.none;
      } else {
        nursingDocFlag = nursingDocs.length > 0 ? FLAG_VALUE.success : FLAG_VALUE.critical;
      }

      let surgDocFlag = FLAG_VALUE.critical;
      if (isNoneSurgeonDocs) {
        surgDocFlag = FLAG_VALUE.none;
      } else {
        surgDocFlag = surgeonDocs.length > 0 ? FLAG_VALUE.success : FLAG_VALUE.critical;
      }

      let anesPreOpDocFlag = FLAG_VALUE.critical;
      if (isNonePreOpFormDocs) {
        anesPreOpDocFlag = FLAG_VALUE.none;
      } else {
        anesPreOpDocFlag = preOpFormDocs.length > 0 ? FLAG_VALUE.success : FLAG_VALUE.critical;
      }

      let anesIntraOpDocFlag = FLAG_VALUE.critical;
      if (isNoneIntraOpFormDocs) {
        anesIntraOpDocFlag = FLAG_VALUE.none;
      } else {
        anesIntraOpDocFlag = intraOpFormDocs.length > 0 ? FLAG_VALUE.success : FLAG_VALUE.critical;
      }

      let anesPostOpDocFlag = FLAG_VALUE.critical;
      if (isNonePostOpFormDocs) {
        anesPostOpDocFlag = FLAG_VALUE.none;
      } else {
        anesPostOpDocFlag = postOpFormDocs.length > 0 ? FLAG_VALUE.success : FLAG_VALUE.critical;
      }

      let ordersPreOpDocFlag = FLAG_VALUE.critical;
      if (isNoneOrdersPreOpDocs) {
        ordersPreOpDocFlag = FLAG_VALUE.none;
      } else {
        ordersPreOpDocFlag = ordersPreOpDocs.length > 0 ? FLAG_VALUE.success : FLAG_VALUE.critical;
      }

      let ordersIntraOpDocFlag = FLAG_VALUE.critical;
      if (isNoneOrdersIntraOpDocs) {
        ordersIntraOpDocFlag = FLAG_VALUE.none;
      } else {
        ordersIntraOpDocFlag = ordersIntraOpDocs.length > 0 ? FLAG_VALUE.success : FLAG_VALUE.critical;
      }

      let ordersPostOpDocFlag = FLAG_VALUE.critical;
      if (isNoneOrdersPostOpDocs) {
        ordersPostOpDocFlag = FLAG_VALUE.none;
      } else {
        ordersPostOpDocFlag = ordresPostOpDocs.length > 0 ? FLAG_VALUE.success : FLAG_VALUE.critical;
      }

      let dcDocFlag = FLAG_VALUE.critical;
      if (isNoneDischargeDocs) {
        dcDocFlag = FLAG_VALUE.none;
      } else {
        dcDocFlag = dischargeDocs.length > 0 ? FLAG_VALUE.success : FLAG_VALUE.critical;
      }

      data.push({
        id: activeRecord._id,
        dos: activeRecord.scheduleStartDate,
        facility: activeRecord.facility,
        anesthesiaStartHour: activeRecord.anesthesiaStartHour,
        anesthesiaStartMinute: activeRecord.anesthesiaStartMinute,
        anesthesiaEndHour: activeRecord.anesthesiaEndHour,
        anesthesiaEndMinute: activeRecord.anesthesiaEndMinute,
        room: facility,
        scheduledTimes,
        surgeons: tmpSurgeons,
        anesthesiologists: { anesthesiologists, crnas },
        crnas,
        patient: activeRecord.patientName || "Not Added yet!",
        procedures: tmpProcs,
        canDownloadPdf: faceSheetDocs.length > 0,
        anesStart: {
          data:
            anesthesiaStartFlag === FLAG_VALUE.success
              ? `${padZero(anesthesiaStartHour)}:${padZero(anesthesiaStartMinute)}`
              : "hh:mm",
          flag: anesthesiaStartFlag,
        },
        surgStart: {
          data:
            surgeryStartFlag === FLAG_VALUE.success
              ? `${padZero(surgeryStartHour)}:${padZero(surgeryStartMinute)}`
              : "hh:mm",
          flag: surgeryStartFlag,
        },
        surgEnd: {
          data:
            surgeryEndFlag === FLAG_VALUE.success
              ? `${padZero(surgeryEndHour)}:${padZero(surgeryEndMinute)}`
              : "hh:mm",
          flag: surgeryEndFlag,
        },
        anesEnd: {
          data:
            anesthesiaEndFlag === FLAG_VALUE.success
              ? `${padZero(anesthesiaEndHour)}:${padZero(anesthesiaEndMinute)}`
              : "hh:mm",
          flag: anesthesiaEndFlag,
        },
        status: {
          i,
          openPopupMenu: handleOnPopupMenu,
          history,
          _id: activeRecord._id,
          status: activeRecord.status,
          statusChangeDate: activeRecord.statusChangeDate,
          caseFlag: activeRecord.caseFlag,
          docsFlag: findFlag(
            faceSheetDocFlag,
            labDocFlag,
            ekgDocFlag,
            imagingDocFlag,
            consultsDocFlag,
            anesConsentDocFlag,
            surgConsentDocFlag,
            nursingDocFlag,
            surgDocFlag,
            anesPreOpDocFlag,
            anesIntraOpDocFlag,
            anesPostOpDocFlag,
            ordersPreOpDocFlag,
            ordersIntraOpDocFlag,
            ordersPostOpDocFlag,
            dcDocFlag
          ),
          showCaseWarning: piFlag || siFlag || tiFlag || patientFlag
        },
        statusChangeDate: activeRecord.statusChangeDate,
      });
    }
    setGridData(data);
  }, [props.caseSchedules, props.caseSchedulesCount, props.userPractices]);

  useEffect(() => {
    setShowLoader(props.isLoadingCaseData);
  }, [props.isLoadingCaseData]);

  useEffect(() => {
    if (scheduleDate !== props.scheduleDate || scheduleMonth !== props.scheduleMonth || scheduleYear !== props.scheduleYear) {
      setScheduleDate(props.scheduleDate);
      setScheduleMonth(props.scheduleMonth);
      setScheduleYear(props.scheduleYear);
    }

    if (scheduleEndDate !== props.scheduleEndDate || scheduleEndMonth !== props.scheduleEndMonth || scheduleEndYear !== props.scheduleEndYear) {
      setScheduleEndDate(props.scheduleEndDate);
      setScheduleEndMonth(props.scheduleEndMonth);
      setScheduleEndYear(props.scheduleEndYear);
    }
  }, [
    props.scheduleDate,
    props.scheduleMonth,
    props.scheduleYear,
    props.scheduleEndDate,
    props.scheduleEndMonth,
    props.scheduleEndYear
  ]);

  // ------------------------ Styles ------------------------
  const styles = StyleSheet.create({
    scheduleContainer: {
      paddingTop: "8px",
      background: "#002448",
      height: "calc(100% - 42px)",
    },
    tabsContainer: {
      borderBottom: "1px solid #001824",
    },
    gridToolbar: {
      position: 'relative',
      height: "40px",
      maxWidth: `${gridWidth}px`,
      display: "flex",
      alignItems: "center",
      justifyContent: 'space-between'
    },
    gridToolbarRightSection: {
      display: "flex",
      padding: "0 8px",
      height: "24px",
    },
    gridTypeIcons: {
      width: "53px",
      height: "28px",
      border: "1px solid #003060",
      borderRadius: "4px",
      display: "flex",
      alignItems: "center",
      padding: "0 1px",
      margin: "-2px",
    },
    gridEllipsisMenuParent: {
      position: "fixed",
      // height: '64px',
      // width: '140px',
      background: "black",
      padding: "1px",
      zIndex: "100",
      border: "1px solid #084080",
    },
    gridEllipsisMenuContainer: {
      minWidth: "140px",
      background: blue[800],
    },
    gridEllipsisMenuItem: {
      display: "flex",
      alignItems: "center",
      height: "40px",
      width: "100%",
      marginTop: "1px",
      paddingLeft: "8px",
      color: "#00A0FF",
      fontSize: "14px",
      // background: '#002448',
      background: blue[800],
      fill: "#00A0FF",
      ":not(.disabled):hover": {
        background: "#0066CC",
      },
    },
    gridEllipsisMenuItemDisabled: {
      color: "#00A0FF80",
      fill: "#00A0FF80",
    },
    dangerGridEllipsisMenuItem: {
      display: "flex",
      alignItems: "center",
      // height: '32px',
      width: "100%",
      marginTop: "1px",
      paddingLeft: "8px",
      // color: '#00A0FF',
      color: "#FF6060",
      fill: "#FF6060",
      fontSize: "14px",
      background: "#002448",
      ":not(.disabled):hover": {
        background: "#0066CC",
      },
    },
    dangerGridEllipsisMenuItemDisabled: {
      color: "#FF606080",
      fill: "#FF606080",
    },
    pfSelector: {
      display: "flex",
      alignItems: "center",
      height: "32px",
      maxWidth: `${gridWidth}px`,
      borderBottom: "1px solid #001824",
      paddingLeft: "8px",
      fontSize: "14px",
      fontWeight: "bold",
    },
    selector: {
      color: blue[400],
    },
    weekDayBtn: {
      width: "55px",
      height: "32px",
      background: blue[700],
      color: blue[400],
      fontSize: "14px",
      fontWeight: "700",
    },
    linkStyles: {
      color: gray[300],
      ":visited": {
        color: gray[300],
      },
    },
    selectedLink: {
      color: blue[500],
    },
  });

  const handleOnAddNewCaseSchedule = () => {
    debugLogToConsole("Firing initiateNewCaseSchedule action!");
    props.initiateNewCaseSchedule({
      selectedFacilityLocation: 'TBD (To Be Determined)'
    });
    localStorage.setItem("selectedPractice", JSON.stringify(selectedPractice));
    history.push("/schedule/create");
  };

  const handleOnPopupMenu = (e, idx) => {
    toggleGridEllipsisMenu(true);
    setCursorX(e.pageX);
    setCursorY(e.pageY);
    setPopupMenuIndex(idx);
  };

  const handleOnChangeCaseStatus = async (status) => {
    const formData = new FormData();
    formData.append("scheduleId", gridData[popupMenuIndex].id);

    const reqHeaders = new Headers();
    const token = localStorage.getItem("token");
    reqHeaders.append("Authorization", `Bearer ${token}`);
    const requestOptions = {
      method: "POST",
      body: formData,
      redirect: "follow",
      headers: reqHeaders,
    };
    const response = await fetch(
      `${BASE_URL}/casesSchedule/cancelCase`,
      requestOptions,
    );
    const result = await response.json();

    if (result.success) {
      console.log("Case submitted successfully!");
      setShowCancelWarningDialog(false);
      console.log('Position 3');
      props.getCaseSchedulesData(
        props.selectedPractice,
        scheduleDate,
        scheduleMonth,
        scheduleYear,
        scheduleEndDate,
        scheduleEndMonth,
        scheduleEndYear,
        currentPage
      );
    }
  };

  const downloadFile = (href, download) => {
    const a = document.createElement('a');
    a.href = href;
    a.download = download;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const handleOnClickDownloadCase = () => {
    if (!gridData[popupMenuIndex].canDownloadPdf) {
      setShowNoPdfWarningDialog(true);
      return;
    }
    setIsDownloadingCaseDocs(true);
    toggleGridEllipsisMenu(false);

    const formData = new FormData();
    formData.append("scheduleId", gridData[popupMenuIndex].id);

    const myHeaders = new Headers();
    const token = localStorage.getItem('token');
    myHeaders.append('Authorization', `Bearer ${token}`);

    const requestOptions = {
      method: 'POST',
      headers: myHeaders,
      body: formData,
      redirect: 'follow'
    };

    fetch(`${BASE_URL}/casesSchedule/downloadCaseDocs`, requestOptions)
      .then(response => response.blob())
      .then((data) => {
        setIsDownloadingCaseDocs(false);
        let fileName = 'caseData.pdf';
        console.log(gridData[popupMenuIndex]);
        toggleGridEllipsisMenu(false);

        // Anesthesiologist
        let anesthesiologistInitials = '';
        if (gridData[popupMenuIndex].anesthesiologists) {
          const tmp = gridData[popupMenuIndex].anesthesiologists.anesthesiologists.split(', ');
          anesthesiologistInitials = `${tmp[1].trim().charAt(0)}${tmp[0].trim()}`;
        }

        // Date of Surgery
        let dos = '';
        if (gridData[popupMenuIndex].dos.length > 0) {
          const tmp = gridData[popupMenuIndex].dos.split('T');
          const dateArr = tmp[0].split('-');
          const month = dateArr[1];
          const date = dateArr[2];
          const year = +dateArr[0] - 2000;
          dos = `${month}-${date}-${year}`;
        }

        // Facility UserName
        let facilityUserName = '';
        if (gridData[popupMenuIndex].facility && Object.keys(gridData[popupMenuIndex].facility).length > 0) {
          facilityUserName = gridData[popupMenuIndex].facility.facilityUserName || '';
        }

        // Anesthesia Start and End Times
        const startHour = gridData[popupMenuIndex].anesthesiaStartHour < 10 ? `0${gridData[popupMenuIndex].anesthesiaStartHour}` : gridData[popupMenuIndex].anesthesiaStartHour;
        const startMinute = gridData[popupMenuIndex].anesthesiaStartMinute < 10 ? `0${gridData[popupMenuIndex].anesthesiaStartMinute}` : gridData[popupMenuIndex].anesthesiaStartMinute;
        const endHour = gridData[popupMenuIndex].anesthesiaEndHour < 10 ? `0${gridData[popupMenuIndex].anesthesiaEndHour}` : gridData[popupMenuIndex].anesthesiaEndHour;
        const endMinute = gridData[popupMenuIndex].anesthesiaEndMinute < 10 ? `0${gridData[popupMenuIndex].anesthesiaEndMinute}` : gridData[popupMenuIndex].anesthesiaEndMinute;
        const times = `${startHour || 'hh'}${startMinute || 'mm'}-${endHour || 'hh'}${endMinute || 'mm'}`;

        // Filename
        fileName = `${anesthesiologistInitials}_${dos}_@${facilityUserName}_${times}`;

        downloadFile(window.URL.createObjectURL(data), `${fileName}.pdf`);
      })
      .catch((err) => console.log(err));
  };

  const handleOnChangeScheduleDate = (year, month, date) => {
    const start = new Date(year, month, date).getTime();
    const end = new Date(scheduleEndYear, scheduleEndMonth, scheduleEndDate).getTime();
    if (end - start >= 0) {
      // setScheduleDate(date);
      // setScheduleMonth(month);
      // setScheduleYear(year);
      props.setCaseScheduleDateRangeFilterStartDate(year, month, date);
      props.setLoadingCaseData(true);
      localStorage.setItem('scheduleDate', date);
      localStorage.setItem('scheduleMonth', month);
      localStorage.setItem('scheduleYear', year);
      console.log('Position 4');
      props.getCaseSchedulesData(
        props.selectedPractice,
        date,
        month,
        year,
        scheduleEndDate,
        scheduleEndMonth,
        scheduleEndYear,
        currentPage
      );
    } else {
      props.setCaseScheduleDateRangeFilterStartDate(scheduleEndYear, scheduleEndMonth, scheduleEndDate);
      props.setLoadingCaseData(true);
      localStorage.setItem('scheduleDate', scheduleEndDate);
      localStorage.setItem('scheduleMonth', scheduleEndMonth);
      localStorage.setItem('scheduleYear', scheduleEndYear);
      console.log('Position 5');
      props.getCaseSchedulesData(
        props.selectedPractice,
        scheduleEndDate,
        scheduleEndMonth,
        scheduleEndYear,
        scheduleEndDate,
        scheduleEndMonth,
        scheduleEndYear,
        currentPage
      );
    }
  };

  const handleOnChangeScheduleEndDate = (year, month, date) => {
    const start = new Date(scheduleYear, scheduleMonth, scheduleDate).getTime();
    const end = new Date(year, month, date).getTime();
    if (end - start >= 0) {
      // setScheduleEndDate(date);
      // setScheduleEndMonth(month);
      // setScheduleEndYear(year);
      props.setCaseScheduleDateRangeFilterEndDate(year, month, date);
      props.setLoadingCaseData(true);
      localStorage.setItem('scheduleEndDate', date);
      localStorage.setItem('scheduleEndMonth', month);
      localStorage.setItem('scheduleEndYear', year);
      console.log('Position 6');
      props.getCaseSchedulesData(
        props.selectedPractice,
        scheduleDate,
        scheduleMonth,
        scheduleYear,
        date,
        month,
        year,
        currentPage
      );
    } else {
      // setScheduleEndDate(scheduleDate);
      // setScheduleEndMonth(scheduleMonth);
      // setScheduleEndYear(scheduleYear);
      props.setCaseScheduleDateRangeFilterEndDate(scheduleYear, scheduleMonth, scheduleDate);
      props.setLoadingCaseData(true);
      localStorage.setItem('scheduleEndDate', date);
      localStorage.setItem('scheduleEndMonth', month);
      localStorage.setItem('scheduleEndYear', year);
      console.log('Position 7');
      props.getCaseSchedulesData(
        props.selectedPractice,
        date,
        month,
        year,
        date,
        month,
        year,
        currentPage
      );
    }
  };

  const handleOnClickDownloadAllCases = () => {
    toggleGridToolbarMenu(false);
    setIsDownloadingPdfBatch(true);
    props.addAppAlert({
      headerText: 'Downloading all case pdf files',
      headerIcon: 'fa fa-info-circle',
      bodyText: 'Batch case download file is in progres. Please do not refresh or leave the page.',
      showAlert: true,
      autoDismiss: true,
      autoDismissTime: 3000
    });
    const formData = new FormData();
    formData.append("scheduleIds", JSON.stringify(gridData.map(el => el.id)));

    const myHeaders = new Headers();
    const token = localStorage.getItem('token');
    myHeaders.append('Authorization', `Bearer ${token}`);

    const requestOptions = {
      method: 'POST',
      headers: myHeaders,
      body: formData,
      redirect: 'follow'
    };

    fetch(`${BASE_URL}/casesSchedule/batchDownloadCaseDocs`, requestOptions)
      .then(response => response.blob())
      .then((data) => {
        console.log(data);
        if (data.type === 'application/zip') {
          setIsDownloadingPdfBatch(false);
          props.addAppAlert({
            headerText: 'Success',
            headerIcon: 'fa fa-info-circle',
            bodyText: 'All case file downloaded as zip file.',
            showAlert: true,
            autoDismiss: true,
            autoDismissTime: 5000
          });
          const zipFileName = `cases_${padZero(scheduleMonth + 1)}${padZero(scheduleDate)}_to_${padZero(scheduleEndMonth + 1)}${padZero(scheduleEndDate)}.zip`;
          downloadFile(window.URL.createObjectURL(data), zipFileName);
        } else {
          setIsDownloadingPdfBatch(false);
          props.addAppAlert({
            headerText: 'Zip File Create Error',
            headerIcon: 'fa fa-info-circle',
            bodyText: 'No case documents exists for any selected cases!',
            showAlert: true,
            autoDismiss: true,
            autoDismissTime: 5000
          });
        }
      })
      .catch((err) => console.log(err));
  };

  const handleOnDeleteCase = async () => {
    const caseScheduleToDeleteId = gridData[popupMenuIndex].id;
    const formData = new FormData();
    formData.append('caseId', caseScheduleToDeleteId);
    // Request Headers incl Authorization
    const reqHeaders = new Headers();
    const token = localStorage.getItem('token');
    reqHeaders.append('Authorization', `Bearer ${token}`);
    const requestOptions = {
      method: 'POST',
      body: formData,
      redirect: 'follow',
      headers: reqHeaders
    };

    const response = await fetch(`${BASE_URL}/casesSchedule/deleteCaseSchedule`, requestOptions);
    const result = await response.json();

    if (result.success) {
      setShowDeleteCaseWarning(false);
      props.setLoadingCaseData(true);
      console.log('Position 8');
      props.getCaseSchedulesData(
        props.selectedPractice,
        scheduleDate,
        scheduleMonth,
        scheduleYear,
        scheduleEndDate,
        scheduleEndMonth,
        scheduleEndYear,
        currentPage
      );
    }
  };

  const scheduleGrid = (
    <>
      {/* ====== Grid Toolbar ====== */}
      <div className={css(styles.gridToolbar)}>
        <div
          style={{ display: isDownloadingPdfBatch ? '' : 'none' }}
          className="loader"
        />
        {/* Select date range */}
        <div className="fx fx__v-center">
          <DatePicker
            zIndex={2600}
            className="ml-2"
            left={5}
            date={scheduleDate}
            month={scheduleMonth}
            year={scheduleYear}
            isPrisitine={false}
            noUnderline
            onChangeDate={handleOnChangeScheduleDate}
          />
          <div
            className="ml-2 mr-2"
            style={{
              paddingTop: '3px',
              fontSize: '14px',
              fontWeight: 'bold'
            }}
          >
            TO
          </div>
          <DatePicker
            zIndex={2600}
            left={5}
            date={scheduleEndDate}
            month={scheduleEndMonth}
            year={scheduleEndYear}
            isPrisitine={false}
            noUnderline
            onChangeDate={handleOnChangeScheduleEndDate}
          />
        </div>
        {/* Icons */}
        <div className={css(styles.gridToolbarRightSection)}>
          {/* Card Vs Rows Grid */}
          <div className={`mr-2 ${css(styles.gridTypeIcons)}`}>
            {/* Rows Icon */}
            <svg
              width="24"
              height="24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              onClick={() => setGridStyle(0)}
            >
              <rect
                width="24"
                height="24"
                rx="2"
                fill={gridStyle === 0 ? "#00A0FF" : "none"}
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M4.477 7.453a1.477 1.477 0 1 1 0-2.953 1.477 1.477 0 0 1 0 2.953Zm4.01-.796h11.667a.847.847 0 1 0 .002-1.692H8.486a.847.847 0 0 0 0 1.692ZM3 11.957a1.477 1.477 0 1 0 2.953 0 1.477 1.477 0 0 0-2.953 0Zm5.487.684h11.667a.847.847 0 1 0 .002-1.692H8.486a.847.847 0 0 0 0 1.692ZM3 17.937a1.477 1.477 0 1 0 2.953 0 1.477 1.477 0 0 0-2.953 0Zm5.487.685h11.667a.847.847 0 1 0 .002-1.692H8.486a.847.847 0 0 0 0 1.692Z"
                fill={gridStyle === 0 ? "#fff" : "#5080B0"}
              />
            </svg>
            {/* Cards Icon */}
            <svg
              width="24"
              height="24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              onClick={() => setGridStyle(1)}
            >
              <rect
                width="24"
                height="24"
                rx="2"
                // fill="#00A0FF"
                fill={gridStyle === 1 ? "#00A0FF" : "none"}
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M4 8h4V4H4v4Zm6 12h4v-4h-4v4Zm-2 0H4v-4h4v4Zm-4-6h4v-4H4v4Zm10 0h-4v-4h4v4Zm2-10v4h4V4h-4Zm-2 4h-4V4h4v4Zm2 6h4v-4h-4v4Zm4 6h-4v-4h4v4Z"
                fill={gridStyle === 1 ? "#fff" : "#5080B0"}
              />
            </svg>
          </div>
          {/* Reload Icon */}
          <div className="mr-2">
            <svg
              width="24"
              height="24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M1.926 0h20.145C23.136 0 23.997.861 24 1.926v20.148A1.925 1.925 0 0 1 22.074 24H1.926A1.925 1.925 0 0 1 0 22.074V1.926C0 .861.861 0 1.926 0ZM15 11.998l4-4 4 4h-3c0 4.42-3.58 8-8 8a7.93 7.93 0 0 1-4.26-1.24l1.46-1.46c.83.45 1.79.7 2.8.7 3.31 0 6-2.69 6-6h-3Zm-3-6c-3.31 0-6 2.69-6 6h3l-4 4-4-4h3c0-4.42 3.58-8 8-8 1.57 0 3.03.46 4.26 1.24l-1.46 1.46a5.87 5.87 0 0 0-2.8-.7Z"
                fill="#96C8FF"
              />
            </svg>
          </div>
          {/* Add Case Icon */}
          <div className="mr-2" onClick={handleOnAddNewCaseSchedule}>
            <svg
              width="24"
              height="24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M2 0a2 2 0 0 0-2 2v20a2 2 0 0 0 2 2h20a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2H2Zm13.84 6.444h3.84c1.066 0 1.92.899 1.92 2.02v11.117c0 1.121-.854 2.02-1.92 2.02H4.32c-1.066 0-1.92-.899-1.92-2.02l.01-11.116c0-1.122.845-2.021 1.91-2.021h3.84V4.423c0-1.122.854-2.021 1.92-2.021h3.84c1.066 0 1.92.899 1.92 2.02v2.022Zm-5.76-.2h3.84v-1.92h-3.84v1.92Zm7.076 8.614h-4.3v4.294a.845.845 0 1 1-1.688 0v-4.294H6.844A.845.845 0 0 1 6 14.011c0-.468.377-.847.844-.847h4.324V8.846a.845.845 0 1 1 1.687 0v4.318h4.301c.467 0 .844.379.844.847a.845.845 0 0 1-.844.847Z"
                fill="#96C8FF"
              />
            </svg>
          </div>
          {/* Submit Case Icon */}
          <div className="mr-2">
            <svg
              width="24"
              height="24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M2 0a2 2 0 0 0-2 2v20a2 2 0 0 0 2 2h20a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2H2Zm13.84 6.444h3.84c1.066 0 1.92.899 1.92 2.02v11.117c0 1.121-.854 2.02-1.92 2.02H4.32c-1.066 0-1.92-.899-1.92-2.02l.01-11.116c0-1.122.845-2.021 1.91-2.021h3.84V4.423c0-1.122.854-2.021 1.92-2.021h3.84c1.066 0 1.92.899 1.92 2.02v2.022Zm-5.76-.2h3.84v-1.92h-3.84v1.92ZM9.438 13.9a.769.769 0 0 0 .564-.25l1.528-1.626v5.848c0 .468.357.85.798.85.439 0 .798-.382.798-.852v-5.846l1.526 1.627a.765.765 0 0 0 1.128 0 .888.888 0 0 0 0-1.202l-2.89-3.077a.765.765 0 0 0-1.127 0L8.873 12.45a.888.888 0 0 0 0 1.202c.15.161.35.249.564.249Z"
                fill="#96C8FF"
              />
            </svg>
          </div>
          {/* Ellipsis Icon */}
          <div
            ref={gridToolbarMenuRef}
            style={{ position: 'relative' }}
          >
            <svg
              width="24"
              height="24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              onClick={() => toggleGridToolbarMenu(true)}
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M2 0a2 2 0 0 0-2 2v20a2 2 0 0 0 2 2h20a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2H2Zm3 9.998a2 2 0 1 0 0 4 2 2 0 0 0 0-4Zm12 2a2 2 0 1 1 4 0 2 2 0 0 1-4 0Zm-5-2a2 2 0 1 0 0 4 2 2 0 0 0 0-4Z"
                fill="#96C8FF"
              />
            </svg>
            <PopupMenu
              top={28}
              right={1}
              position="absolute"
              zIndex='1501'
              visible={showGridToolbarMenu}
              parent={gridToolbarMenuRef}
              onOutsideClick={() => {
                toggleGridToolbarMenu(false);
              }}
            >
              <div className={css(styles.gridEllipsisMenuContainer)}>
                {/* Download All Case Schedules PDFs */}
                <div
                  className={css(styles.gridEllipsisMenuItem)}
                  onClick={handleOnClickDownloadAllCases}
                >
                  <span className="cursor__pointer ml-2">Download All PDFs</span>
                </div>
              </div>
            </PopupMenu>
          </div>
        </div>
      </div>
      {/* ====== Grid Popup Menu ====== */}
      <div
        ref={gridEllipsisMenu}
        className={css(styles.gridEllipsisMenuParent)}
        style={{
          top: window.innerHeight - cursorY >= 166 ? `${cursorY + 12}px` : "",
          bottom: window.innerHeight - cursorY < 166 ? "40px" : "",
          left: cursorX - 130,
          display: showGridEllipsisMenu ? "block" : "none",
        }}
      >
        <PopupMenu
          top={0}
          left={0}
          position="relative"
          visible={showGridEllipsisMenu}
          parent={gridEllipsisMenu}
          onOutsideClick={() => {
            toggleGridEllipsisMenu(false);
          }}
        >
          <div className={css(styles.gridEllipsisMenuContainer)}>
            {/* Open Schedule */}
            <div
              className={css(styles.gridEllipsisMenuItem)}
              onClick={() => {
                const readOnlyMode =
                  gridData[popupMenuIndex].status.status === "cancelled" ||
                  gridData[popupMenuIndex].status.status === "sent";
                history.push(`/schedule/${gridData[popupMenuIndex].id}/${readOnlyMode}`);
              }}
            >
              <svg
                width="20"
                height="20"
                fill="inherit"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M18 4.21h-4V2.106C14 .937 13.11 0 12 0H8C6.89 0 6 .937 6 2.105v2.106H2C.89 4.21.01 5.147.01 6.316L0 17.895C0 19.063.89 20 2 20h16c1.11 0 2-.937 2-2.105V6.315c0-1.168-.89-2.104-2-2.104ZM12 4H8v-2h4v2Zm2.846 3.337h-3.587a.491.491 0 1 0 0 .982h2.359l-2.594 2.59a.49.49 0 1 0 .695.694l2.634-2.63v2.336a.491.491 0 0 0 .983 0V7.826a.49.49 0 0 0-.49-.49Zm-5.43 9.017h-2.36l2.594-2.59a.49.49 0 1 0-.695-.695L6.32 15.7v-2.337a.491.491 0 0 0-.983 0v3.482c0 .272.22.491.492.491h3.587a.491.491 0 0 0 0-.982Z"
                  fill="inherit"
                />
              </svg>
              <span className="cursor__pointer ml-2">Open Schedule</span>
            </div>
            {/* Download PDF */}
            <div
              className={css(styles.gridEllipsisMenuItem)}
              onClick={handleOnClickDownloadCase}
            >
              <i className="fa fa-file-pdf-o font-20" />
              <span className="cursor__pointer ml-2">Download PDF</span>
            </div>
            {/* --- Submit Case (Sent to Cases Grid) --- */}
            {/* <div
              className={`
                ${gridData &&
                  gridData.length > 0 &&
                  popupMenuIndex > -1 &&
                  (gridData[popupMenuIndex].status.status === "sent" ||
                    gridData[popupMenuIndex].status.status === "cancelled")
                  ? "disabled"
                  : ""
                }
                ${css(
                  styles.gridEllipsisMenuItem,
                  gridData &&
                  gridData.length > 0 &&
                  popupMenuIndex > -1 &&
                  (gridData[popupMenuIndex].status.status === "sent" ||
                    gridData[popupMenuIndex].status.status === "cancelled" ||
                    gridData[popupMenuIndex].status.caseFlag ===
                    FLAG_VALUE.critical) &&
                  styles.gridEllipsisMenuItemDisabled,
                )}`}
              onClick={() => {
                if (
                  !(
                    gridData &&
                    gridData.length > 0 &&
                    popupMenuIndex > -1 &&
                    (gridData[popupMenuIndex].status.status === "sent" ||
                      gridData[popupMenuIndex].status.status === "cancelled" ||
                      gridData[popupMenuIndex].status.caseFlag ===
                      FLAG_VALUE.critical)
                  )
                ) {
                  setShowFinalizeWarningDialog(true);
                }
              }}
            >
              <svg
                width="20"
                height="20"
                fill="inherit"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M18 4.21h-4V2.106C14 .937 13.11 0 12 0H8C6.89 0 6 .937 6 2.105v2.106H2C.89 4.21.01 5.147.01 6.316L0 17.895C0 19.063.89 20 2 20h16c1.11 0 2-.937 2-2.105V6.315c0-1.168-.89-2.104-2-2.104ZM12 4H8v-2h4v2Zm-4.082 7.717a.793.793 0 0 1-1.175 0 .925.925 0 0 1 0-1.252l3.009-3.206a.797.797 0 0 1 1.174 0l3.011 3.206a.925.925 0 0 1 0 1.252.797.797 0 0 1-1.175 0l-1.59-1.695v6.09c0 .49-.373.888-.83.888-.46 0-.832-.398-.832-.886v-6.092l-1.592 1.694Z"
                  fill="inherit"
                />
              </svg>
              <span className="cursor__pointer ml-2">Submit Case</span>
            </div> */}
            {/* Cancel Schedule */}
            <div
              className={`
                ${gridData &&
                  gridData.length > 0 &&
                  popupMenuIndex > -1 &&
                  ((gridData[popupMenuIndex] && gridData[popupMenuIndex].status && gridData[popupMenuIndex].status.stauts && gridData[popupMenuIndex].status.status === "sent") ||
                  (gridData[popupMenuIndex] && gridData[popupMenuIndex].status && gridData[popupMenuIndex].status.status && gridData[popupMenuIndex].status.status === "cancelled"))
                  ? "disabled"
                  : ""
                }
                ${css(
                  styles.gridEllipsisMenuItem,
                  styles.dangerGridEllipsisMenuItem,
                  gridData &&
                  gridData.length > 0 &&
                  popupMenuIndex > -1 &&
                  ((gridData[popupMenuIndex] && gridData[popupMenuIndex].status && gridData[popupMenuIndex].status.stauts && gridData[popupMenuIndex].status.status === "sent") ||
                  (gridData[popupMenuIndex] && gridData[popupMenuIndex].status && gridData[popupMenuIndex].status.stauts && gridData[popupMenuIndex].status.status === "cancelled")) &&
                  styles.dangerGridEllipsisMenuItemDisabled,
                )}`}
              onClick={() => {
                if (gridData[popupMenuIndex].status.status !== "cancelled") {
                  setShowCancelWarningDialog(true);
                }
              }}
            >
              <svg
                width="20"
                height="19"
                // fill="none"
                fill="inherit"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M18 4h-4V2c0-1.11-.89-2-2-2H8C6.89 0 6 .89 6 2v2H2C.89 4 .01 4.89.01 6L0 17c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2Zm-6 0H8V2h4v2Zm-8 8c0-3.312 2.688-6 6-6s6 2.688 6 6-2.688 6-6 6-6-2.688-6-6Zm6-4.8A4.799 4.799 0 0 0 5.2 12c0 1.11.378 2.13 1.014 2.94l6.726-6.726A4.741 4.741 0 0 0 10 7.2Zm0 9.6c-1.11 0-2.13-.378-2.94-1.014l6.726-6.726A4.741 4.741 0 0 1 14.8 12c0 2.652-2.148 4.8-4.8 4.8Z"
                  // fill="#FF6060"
                  fill="inherit"
                />
              </svg>
              <span className="cursor__pointer ml-2">Cancel Case</span>
            </div>
            {/* Delete Schedule */}
            <div
              className={`
                ${gridData &&
                  gridData.length > 0 &&
                  popupMenuIndex > -1 &&
                  ((gridData[popupMenuIndex] && gridData[popupMenuIndex].status && gridData[popupMenuIndex].status.stauts && gridData[popupMenuIndex].status.status === "sent") ||
                  (gridData[popupMenuIndex] && gridData[popupMenuIndex].status && gridData[popupMenuIndex].status.status && gridData[popupMenuIndex].status.status === "cancelled"))
                  ? "disabled"
                  : ""
                }
                ${css(
                  styles.gridEllipsisMenuItem,
                  styles.dangerGridEllipsisMenuItem,
                  gridData &&
                  gridData.length > 0 &&
                  popupMenuIndex > -1 &&
                  gridData[popupMenuIndex] &&
                  gridData[popupMenuIndex].status !== undefined &&
                  gridData[popupMenuIndex].status.stauts !== undefined &&
                  gridData[popupMenuIndex].status.status === "cancelled" &&
                  styles.dangerGridEllipsisMenuItemDisabled,
                )}`}
              onClick={() => {
                if (gridData[popupMenuIndex].status.status !== "cancelled") {
                  toggleGridEllipsisMenu(false);
                  setShowDeleteCaseWarning(true);
                }
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="inherit"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  fill="inherit"
                  d="M19.7668 3.10346C19.6154 2.95109 19.3952 2.86615 19.1815 2.86615H15.074V1.57844C15.074 0.707562 14.3708 0 13.5054 0H6.49378C5.62832 0 4.92516 0.707562 4.92516 1.57844V2.86615H0.79671C0.358541 2.86615 0 3.22431 0 3.66785C0 4.11139 0.355931 4.46955 0.79671 4.46955H1.49987V18.4216C1.49987 19.2924 2.20303 20 3.06849 20H17.1543C18.0198 20 18.7229 19.2924 18.7229 18.4216V4.46955H19.1811C19.6193 4.46955 20 4.11139 20 3.66785C20.0004 3.45505 19.9182 3.25583 19.7668 3.10346ZM2.27307 19.5945C2.30439 19.6286 2.33746 19.6594 2.37097 19.6901L2.37093 19.6901C2.33744 19.6594 2.30395 19.6286 2.27307 19.5945ZM2.48512 19.7173C2.51668 19.7518 2.5488 19.7863 2.58036 19.8129C2.56062 19.7963 2.54199 19.7766 2.52226 19.7557L2.52224 19.7557C2.51042 19.7431 2.4982 19.7302 2.48512 19.7173ZM13.499 1.58482H6.49182V2.88129H13.499V1.58482ZM17.1524 18.4215H3.0692V4.47173H5.09861H17.1524V18.4215ZM9.18899 7.96382C9.18899 7.51809 9.54709 7.15774 9.99005 7.15774C10.433 7.15774 10.7911 7.51852 10.7911 7.96382V15.4427C10.7911 15.8884 10.433 16.2488 9.99005 16.2488C9.54709 16.2488 9.18899 15.8884 9.18899 15.4427V7.96382ZM5.65225 7.15774C5.2093 7.15774 4.85119 7.51809 4.85119 7.96382V15.4427C4.85119 15.8884 5.2093 16.2488 5.65225 16.2488C6.09521 16.2488 6.45331 15.8884 6.45331 15.4427V7.96382C6.45331 7.51852 6.09477 7.15774 5.65225 7.15774ZM13.4821 7.96382C13.4821 7.51809 13.8402 7.15774 14.2832 7.15774C14.7262 7.15774 15.0843 7.51852 15.0843 7.96382V15.4427C15.0843 15.8884 14.7262 16.2488 14.2832 16.2488C13.8402 16.2488 13.4821 15.8884 13.4821 15.4427V7.96382Z"
                />
              </svg>
              <span className="cursor__pointer ml-2">Delete Case</span>
            </div>
          </div>
        </PopupMenu>
      </div>
      {/* ====== Schedule Case Grid ====== */}
      <DataGrid
        loader={showLoader}
        icons={ICONS}
        headerDefs={HEADER_DEFS}
        rowData={gridData}
        filters={filters}
        gridWidth={gridWidth}
        gridHeight={gridHeight}
        gridHeaderHeight={48}
        gridFooterHeight={0}
        gridContainerHeight="730px"
        rowHeight={64}
        rowsPerPage={DOCS_PER_PAGE}
        defaultCellStyle={DEFAULT_CELL_STYLE}
        showFooter={false}
        showPager
        showEllipsisCol={false}
        enableHover
        totalPages={Math.ceil(docsCount / DOCS_PER_PAGE)}
        currentPage={currentPage}
        isMobileLayout={isMobileLayout}
        onClickCell={() => {
          console.log("Cell click handler not done yet!");
        }}
        onApplyFilter={() => {
          console.warn("Handler not set for onApplyFilter prop.");
        }}
        onResetCol={() => {
          console.warn("Handler not set for onResetCol prop.");
        }}
        onLoadPage={(page) => {
          setCurrentPage(page);
        }}
        onColFilterToggle={() => {
          console.warn("Handler not set for onColFilterToggle prop.");
        }}
        onFilterTypeUpdate={() => {
          console.warn("Handler not set for onFilterTypeUpdate prop.");
        }}
        onColPinChange={() => {
          console.warn("Handler not set for onColPinChange prop.");
        }}
        onResize={() => {
          console.warn("Handler not set for onResize prop.");
        }}
        onColVisibilityChange={() => {
          console.warn("Handler not set for onColVisibilityChange prop.");
        }}
        onFilterChange={() => {
          console.warn("Handler not set for onFilterChange prop.");
        }}
        onChangeTextAlignment={() => {
          console.warn("Handler not set for onChangeTextAlignment prop.");
        }}
        onResetGrid={() => {
          console.warn("Handler not set for onResetGrid prop.");
        }}
        onSort={() => {
          console.warn("Handler not set for onSort prop.");
        }}
        onColConfig={() => {
          console.warn("Handler not set for onColConfig prop.");
        }}
      />
    </>
  );

  return (
    <div className={css(styles.scheduleContainer)} ref={gridParentRef}>
      {/* Cancel Schedule WarningDialog */}
      {showCancelWarningDialog && (
        <WarningDialog
          show={showCancelWarningDialog}
          title="Cancel Schedule"
          message="Do you really want to cancel?"
          onCancel={() => setShowCancelWarningDialog(false)}
          onContinue={() => {
            console.log("Set status to cancel");
            handleOnChangeCaseStatus("cancelled");
          }}
        />
      )}

      {/* Delete case warning dialog */}
      {showDeleteCaseWarning &&
        <WarningDialog
          show={showDeleteCaseWarning}
          title="Delete Case Schedule"
          message="Do you really want to delete this case scheule?"
          onCancel={() => setShowDeleteCaseWarning(false)}
          onContinue={() => {
            handleOnDeleteCase();
          }}
        />
      }

      {showNoPdfWarningDialog && (
        <WarningDialog
          show={showNoPdfWarningDialog}
          title="Cannot Download PDF"
          message="No case documents have been uploaded. Cannot download PDF."
          onCancel={() => setShowNoPdfWarningDialog(false)}
          onContinue={() => {
            setShowNoPdfWarningDialog(false);
          }}
        />
      )}

      {showFinalizeWarningDialog && (
        <WarningDialog
          show={showFinalizeWarningDialog}
          title="Finalize Schedule"
          message="Do you really want to finalize?"
          onCancel={() => setShowFinalizeWarningDialog(false)}
          onContinue={() => {
            console.log("Set status to finalized");
            toggleGridEllipsisMenu(false);
            setShowFinalizeWarningDialog(false);
            submitCase(gridData[popupMenuIndex].id);
            // console.log(gridData[popupMenuIndex]);
            // submitCase(gridData[popupMenuIndex]._id);
          }}
        />
      )}

      {/* ====== Select Practice ====== */}
      <div className={css(styles.pfSelector)}>
        {/* -- Practices -- */}
        <div
          ref={practiceMenuRef}
          className={css(styles.selector)}
          style={{ position: "relative", width: "180px" }}
          onClick={() => setIsPracticeMenuOpen(!isPracticeMenuOpen)}
        >
          <div className="cursor__pointer">
            {Object.keys(selectedPractice).length > 0
              ? (<div className="fx fx__v-center" style={{ width: "320px" }}>
                  <img
                    style={{ maxHeight: "18px" }}
                    src={selectedPractice.avatarImageUrl}
                  />
                  <span className="ml-2">{selectedPractice.practiceName}</span>
                  <span className="ml-2">{isPracticeMenuOpen ? "▲" : "▼"}</span>
                </div>
                )
              : (
                `Select Practice ${isPracticeMenuOpen ? "▲" : "▼"}`
                )}
          </div>
          <PopupMenu
            top={20}
            left={0}
            position="absolute"
            zIndex="2501"
            visible={isPracticeMenuOpen}
            parent={practiceMenuRef}
            onOutsideClick={() => {
              setIsPracticeMenuOpen(false);
            }}
          >
            <div
              style={{ width: "320px" }}
              className={css(styles.gridEllipsisMenuContainer)}
            >
              {props.userPractices.map((el, idx) => (
                <div
                  key={el._id}
                  className={css(styles.gridEllipsisMenuItem)}
                  onClick={() => {
                    setIsPracticeMenuOpen(false);
                    props.setSelectedPractice(idx);
                    if (pathname.endsWith('/gantt')) {
                      props.toggleCbLoader(true);
                      props.loadCaseBlocksData({
                        practiceNpi: el.npi,
                        scheduleDate: props.selectedCaseBlockDate,
                        scheduleMonth: props.selectedCaseBlockMonth,
                        scheduleYear: props.selectedCaseBlockYear
                      });
                      props.getProvidersSchedule({
                        pUsers: props.selectedPractice.users,
                        practiceNpi: props.selectedPractice.npi,
                        scheduleDate: props.selectedCaseBlockDate,
                        scheduleMonth: props.selectedCaseBlockMonth,
                        scheduleYear: props.selectedCaseBlockYear
                      });
                    } else {
                      props.setLoadingCaseData(true);
                      console.log('Position 9');
                      props.getCaseSchedulesData(
                        props.userPractices[idx],
                        scheduleDate,
                        scheduleMonth,
                        scheduleYear,
                        scheduleEndDate,
                        scheduleEndMonth,
                        scheduleEndYear,
                        currentPage
                      );
                    }
                  }}
                >
                  {/* <img style={{ maxHeight: "22px" }} src={el.avatarImageUrl} /> */}
                  {/* <span className="ml-2">{el.practiceName}</span> */}
                  <span className="ml-1">{el.displayName}</span>
                </div>
              ))}
            </div>
          </PopupMenu>
        </div>
      </div>

      {/* ====== Tabs ====== */}
      <div className={`fx pl-2 ${css(styles.tabsContainer)}`}>
        <TabsContainer
          dropDownId="d3434"
          showPlusIcon={false}
          isDrawer={false}
          shiftMethod="minWidth"
          minWidth={610}
          showDropdown={false}
          containerStyles={{ width: "100%", borderBottom: "none" }}
        >
          {/* Schedule */}
          <TabItem
            key="schedule_tab"
            tabKey="stab_001"
            parentKey="sd_1212"
            text="Schedule"
            isActive={
              pathname.endsWith("schedule") ||
              pathname.endsWith("schedule/") ||
              pathname.endsWith("main")
            }
            showTabIcon={false}
            showSvgIcon={false}
            style={{
              width: "60px",
              minWidth: "60px",
              maxWidth: "60px",
              background: "#002448",
              height: "32px",
              color:
                pathname.endsWith("schedule") ||
                  pathname.endsWith("schedule/") ||
                  pathname.endsWith("main")
                  ? "#00a0ff"
                  : "",
              borderBottom:
                pathname.endsWith("schedule") ||
                  pathname.endsWith("schedule/") ||
                  pathname.endsWith("main")
                  ? "1px solid #00a0ff"
                  : "none",
            }}
            textStyles={TABS_TEXT_STYLES}
            onActivate={() => {
              // setActiveTab(0);
              history.push(`${url}/main`);
            }}
          >
            Schedule
          </TabItem>
          {/* Availability */}
          <TabItem
            key="availability_tab"
            tabKey="savailability_002"
            parentKey="sd_1414"
            text="Availability"
            isActive={pathname.endsWith("ppa")}
            showTabIcon={false}
            showSvgIcon={false}
            style={{
              width: "70px",
              minWidth: "70px",
              maxWidth: "70px",
              background: "#002448",
              height: "32px",
              color: pathname.endsWith("ppa") ? "#00a0ff" : "",
              borderBottom: pathname.endsWith("ppa")
                ? "1px solid #00a0ff"
                : "none",
              // borderBottom: 'none'
            }}
            textStyles={TABS_TEXT_STYLES}
            onActivate={() => {
              // setActiveTab(1);
              history.push(`${url}/ppa`);
            }}
          >
            Availability
          </TabItem>
          {/* Hours (Schedule 2 ) */}
          <TabItem
            key="hours_tab"
            tabKey="shours_002"
            parentKey="sd_1313"
            text="Hours"
            isActive={pathname.endsWith("gantt")}
            showTabIcon={false}
            showSvgIcon={false}
            style={{
              width: "75px",
              minWidth: "75px",
              maxWidth: "75px",
              background: "#002448",
              height: "32px",
              color: pathname.endsWith("gantt") ? "#00a0ff" : "",
              borderBottom: pathname.endsWith("gantt")
                ? "1px solid #00a0ff"
                : "none",
            }}
            textStyles={TABS_TEXT_STYLES}
            onActivate={() => {
              // setActiveTab(2);
              history.push(`${url}/gantt`);
            }}
          >
            Schedule 2
          </TabItem>
        </TabsContainer>
        {/* For debugging -- delete one user filtering is done for Schedule grid */}
        {localStorage.getItem("usernameOrEmail") && (
          <span className="pr-2" style={{ display: "none", color: "yellow" }}>
            {localStorage.getItem("usernameOrEmail")}
          </span>
        )}
      </div>

      <Switch>
        <Route exact path={path}>
          {isDownloadingCaseDocs &&
            <Loader iconType="cog" iconSize="5x" text="Creating Case Documents PDF File"/>
          }
          {scheduleGrid}
        </Route>
        <Route path={`${path}/main`}>{scheduleGrid}</Route>
        <Route path={`${path}/ppa`}>
          <PPAGrid practice={selectedPractice} />
        </Route>
        <Route path={`${path}/gantt`}>
          <GanttGrid />
        </Route>
      </Switch>
    </div>
  );
};

function mapStateToProps({ caseSchedule, user }) {
  return {
    ...caseSchedule,
    ...user
  };
}

// export default Schedule;
export default connect(mapStateToProps, {
  setLoadingCaseData,
  getCaseSchedulesData,
  initiateNewCaseSchedule,
  setSelectedPractice,
  toggleCbLoader,
  loadCaseBlocksData,
  getProvidersSchedule,
  addAppAlert,
  setCaseScheduleDateRangeFilterStartDate,
  setCaseScheduleDateRangeFilterEndDate
})(Schedule);
